import { type JanusCtx, type Row, HandBack, ec, ecLean, record } from '../ability/context.js';
import { statusItemId } from './expiry.js';
import { encounterField as ftEncounterField, posCoord as ftPosCoord } from '../../shared/array-types.js';
import { forcedMovementReaders, layerKeysDeep } from '../ability/movement/check-push-and-pull-after-hit.js';
import { predictFall, SPEED } from '../movement/fall-check.js';
import { resolveFallDamage, FALLING_DAMAGE_ID } from '../ability/movement/fall-damage.js';
import { cpiFoldDeps } from '../cpi/heal.js';
import { fallingShapeSupportFloor, type RestShape } from '../inventory/floor-move.js';
import { recompileFormRemoval } from '../compile/form-revert.js';
import { statusRows } from './expiry.js';
import { readItemField } from '../compile/item-field-reader.js';
import { applyMemberStatusRoots } from '../compile/member-status-recompile.js';

/** Hold the same nested status/form tree the support planner walks. Each missing id is fetched once; failed reads stay unheld. */
export async function hydrateRemovalSupportDefinitions(statuses: readonly Row[],
  read: Pick<JanusCtx['read'], 'entries' | 'definition'>, fetchDefinitions: (ids: readonly string[]) => Promise<void>): Promise<void> {
  const requested = new Set<string>();
  for (;;) {
    const missing = new Set<string>();
    for (const status of statuses) {
      const id = statusItemId(status), held = read.definition(id), definition = held ?? record(status.status_arr);
      if (!held && /^[0-9A-F]{8}(-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(id)) missing.add(id);
      if (definition) layerKeysDeep({ read }, definition, new Set([id]), { nodes: 0, missing });
    }
    const wanted = [...missing].filter(id => !requested.has(id));
    if (!wanted.length) return;
    for (const id of wanted) requested.add(id);
    await fetchDefinitions(wanted);
  }
}

/** Removal changes support on the same browser snapshot as the selected status. */
export function resolveRemovalSupport(ctx: JanusCtx, ownerId: string, removed: Row): void {
  const member = ctx.members.get(ownerId);
  if (!member || Number(ecLean(member, 'encounter_creature_dead') ?? 0) === 1) return;
  const itemId = statusItemId(removed), definition = ctx.read.definition(itemId) ?? record(removed.status_arr);
  // An NPC's held body buckets still describe its form here. Settle support
  // against the same native source window the later saved-frame projection
  // installs, including all connection losses already decided by this action.
  const original = ctx.originals.get(ownerId);
  const removedForm = !record(member.player_characters) && readItemField(definition, 'item_type') === 'creature';
  const projection = removedForm && original ? recompileFormRemoval({ ...member, statuses: original.statuses },
    statusRows(original, 'target').filter(status => !ctx.read.statuses(member).some(held =>
      readItemField(held, 'status_id') === readItemField(status, 'status_id'))).map(status => ({ kind: 'remove',
        statusId: String(readItemField(status, 'status_id') ?? ''), statusItemId: statusItemId(status) })), ctx.read.definition) : null;
  if (projection && !projection.ok) throw new HandBack('status-removal:' + projection.reason);
  if (projection?.ok) {
    member.computed = { ...record(member.computed), creature: projection.creature, vitality: projection.vitality };
    applyMemberStatusRoots(member, projection);
  }
  const transport = itemId === 'FF8D219A-2A83-0EE5-A222-E748753E8D0B' || itemId === 'A1F1FAF2-DA1E-D537-36C2-2B74DC82CB12';
  const keys = definition ? layerKeysDeep(ctx, definition) : [];
  if (keys === null) throw new HandBack('status-removal:support-definition-tree-not-held');
  if (!transport && !keys.some(k => /^speed_|^movement_|shapeshift/.test(k))) return;
  const raw = ftEncounterField(ctx.encounter, 'encounter_shape');
  const shape = record(typeof raw === 'string' ? (raw === '' ? null : JSON.parse(raw)) : raw) as RestShape | null;
  const x = ftPosCoord(member, 'x_pos') ?? 0, y = ftPosCoord(member, 'y_pos') ?? 0, feet = ftPosCoord(member, 'z_pos') ?? 0;
  if (!transport && shape && Array.isArray(shape.boxes) && shape.boxes.length > 0) {
    const floor = fallingShapeSupportFloor(shape, x, y, feet, 0.05);
    if (floor !== null && Math.abs(feet - floor) <= 0.05) return;
  }
  const compiled = projection?.ok ? { ok: true as const, speeds: record(projection.creature.speeds) ?? {} }
    : cpiFoldDeps(id => ctx.read.definition(id)).compileSpeeds?.(member, true);
  if (!compiled?.ok) throw new HandBack(`status-removal:speed-compile:${compiled?.reason ?? 'unavailable'}`);
  const speeds = compiled.speeds;
  if (transport) {
    const natural = Object.entries(SPEED).filter(([name]) => name !== 'jumping')
      .sort(([a], [b]) => Number(record(speeds[b])?.total ?? 0) - Number(record(speeds[a])?.total ?? 0)
        || (a === 'flying' ? -1 : b === 'flying' ? 1 : 0));
    ec(member).preferred_speed_type = natural.find(([name]) => Number(record(speeds[name])?.total ?? 0) > 0)?.[1] ?? SPEED.walking;
    if (shape?.boxes?.length) {
      const floor = fallingShapeSupportFloor(shape, x, y, feet, 0.05);
      if (floor !== null && Math.abs(feet - floor) <= 0.05) return;
    }
  }
  resolveLandingSupport(ctx, ownerId, speeds);
}

/** Shared support/fall settlement for a row already placed by a browser move. */
export function resolveLandingSupport(ctx: JanusCtx, ownerId: string, speeds?: Row): void {
  const member = ctx.members.get(ownerId);
  if (!member || Number(ecLean(member, 'encounter_creature_dead') ?? 0) === 1) return;
  const raw = ftEncounterField(ctx.encounter, 'encounter_shape');
  const shape = record(typeof raw === 'string' ? (raw === '' ? null : JSON.parse(raw)) : raw) as RestShape | null;
  const base = forcedMovementReaders(ctx);
  const read = { ...base, value: (row: Row, key: string): unknown => speeds && row === member && /^speed_/.test(key)
    ? record(speeds[key.slice(6)])?.total ?? 0 : base.value(row, key),
    position: (row: Row): [number, number, number] => [ftPosCoord(row, 'x_pos') ?? 0, ftPosCoord(row, 'y_pos') ?? 0, ftPosCoord(row, 'z_pos') ?? 0] };
  const support = predictFall({ row: member, encounter: ctx.encounter, shape, legacyCentre: true }, read);
  if (support.kind === 'handback') throw new HandBack(`status-removal:${support.reason}`);
  const z = support.kind === 'fall' ? support.landingZ : support.snapZ;
  if (z !== undefined) { (member.position ??= { ...record(member.pos_arr) } as Row); (member.position as Row).z_pos = z; ec(member).z_pos = z; }
  if (support.kind === 'fall') {
    const damage = ctx.read.definition(FALLING_DAMAGE_ID);
    if (!damage) throw new HandBack('status-removal:fall-definition-not-held');
    resolveFallDamage(ctx, { abilityId: FALLING_DAMAGE_ID, casterId: ownerId, targetIds: [ownerId], rawDiceMod: 0,
      rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null, castingTargets: ownerId }, damage, member, support.fallFt);
  }
}
